import { useEffect, useState } from "react";
import { listCampaigns, getCampaign } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { CheckCircle, XCircle, X } from "@phosphor-icons/react";

export default function History() {
  const [campaigns, setCampaigns] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    listCampaigns().then(setCampaigns).catch(() => {});
  }, []);

  const openDetail = async (id) => {
    const c = await getCampaign(id);
    setSelected(c);
  };

  return (
    <div data-testid="history-page">
      <PageHeader eyebrow="Audit log" title="History" subtitle="Every send, with per-recipient delivery status." />

      <div className="p-10">
        {campaigns.length === 0 ? (
          <div className="border border-[#E5E7EB] bg-[#F9FAFB] p-16 text-center text-[#52525B]">
            No sends recorded yet.
          </div>
        ) : (
          <div className="border border-[#E5E7EB] bg-white">
            <table className="w-full text-sm" data-testid="campaigns-table">
              <thead>
                <tr className="text-left text-[11px] tracking-[0.15em] uppercase text-[#A1A1AA] border-b border-[#E5E7EB]">
                  <th className="px-4 py-3 font-semibold">Subject</th>
                  <th className="px-4 py-3 font-semibold">From name</th>
                  <th className="px-4 py-3 font-semibold">Reply-To</th>
                  <th className="px-4 py-3 font-semibold">Sent</th>
                  <th className="px-4 py-3 font-semibold">Failed</th>
                  <th className="px-4 py-3 font-semibold">Date</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((c) => (
                  <tr
                    key={c.id}
                    data-testid={`campaign-row-${c.id}`}
                    onClick={() => openDetail(c.id)}
                    className="border-b border-[#F3F4F6] hover:bg-[#F9FAFB] cursor-pointer transition-colors duration-150"
                  >
                    <td className="px-4 py-3 font-medium max-w-xs truncate">{c.subject}</td>
                    <td className="px-4 py-3 text-[#52525B]">{c.from_name}</td>
                    <td className="px-4 py-3 font-mono text-xs text-[#52525B]">{c.reply_to || "—"}</td>
                    <td className="px-4 py-3 text-[#10B981] font-semibold">{c.sent_count}</td>
                    <td className="px-4 py-3 text-[#EF4444] font-semibold">{c.failed_count}</td>
                    <td className="px-4 py-3 text-[#A1A1AA] font-mono text-xs">
                      {new Date(c.created_at).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selected && (
        <div className="fixed inset-0 bg-black/40 flex justify-end z-50" onClick={() => setSelected(null)}>
          <div
            data-testid="campaign-detail"
            className="w-full max-w-lg bg-white h-full overflow-y-auto border-l border-[#E5E7EB] animate-fade-up"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-5 border-b border-[#E5E7EB] sticky top-0 bg-white">
              <div className="font-display font-bold text-lg truncate pr-4">{selected.subject}</div>
              <button data-testid="close-detail-btn" onClick={() => setSelected(null)} className="text-[#A1A1AA] hover:text-[#09090B]">
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.15em] text-[#A1A1AA] mb-1">From name</div>
                  <div className="font-medium">{selected.from_name}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.15em] text-[#A1A1AA] mb-1">From email</div>
                  <div className="font-mono text-xs break-all">{selected.from_email}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.15em] text-[#A1A1AA] mb-1">Reply-To</div>
                  <div className="font-mono text-xs break-all">{selected.reply_to || "—"}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.15em] text-[#A1A1AA] mb-1">Sent</div>
                  <div className="font-medium">{selected.sent_count}/{selected.total}</div>
                </div>
              </div>

              <div>
                <div className="text-[10px] uppercase tracking-[0.15em] text-[#A1A1AA] mb-2">Recipients</div>
                <div className="border border-[#E5E7EB]">
                  {selected.recipients?.map((r, i) => (
                    <div key={i} className="flex items-center gap-2 px-3 py-2 border-b border-[#F3F4F6] text-sm last:border-0">
                      {r.status === "sent" ? (
                        <CheckCircle size={16} weight="fill" color="#10B981" />
                      ) : (
                        <XCircle size={16} weight="fill" color="#EF4444" />
                      )}
                      <span className="font-mono text-xs break-all flex-1">{r.email}</span>
                      {r.error && <span className="text-[10px] text-[#EF4444] truncate max-w-[40%]">{r.error}</span>}
                    </div>
                  ))}
                </div>
              </div>

              {selected.html_body && (
                <div>
                  <div className="text-[10px] uppercase tracking-[0.15em] text-[#A1A1AA] mb-2">Body preview</div>
                  <div className="border border-[#E5E7EB] p-4 text-sm" dangerouslySetInnerHTML={{ __html: selected.html_body }} />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
